import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, orderBy } from "firebase/firestore";
import { Clock, CheckCircle, AlertCircle, ChevronRight, FileText, Truck, X, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import StatusBadge from './components/StatusBadge.js';

const AccountsApprovals = ({ db, user }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    // Fetch all MOCs except drafts to track full history
    const q = query(
      collection(db, "dispatches"), 
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log("MOC Queue Snapshot received. Count:", querySnapshot.size);
      const mocList = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(moc => moc.dispatchStatus && moc.dispatchStatus !== 'moc_draft');
      console.log("MOC List mapped:", mocList);
      setMocs(mocList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching MOC approval queue: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const formatDate = (dateString) => {
      if (!dateString) return '---';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'moc_pending_finance':
        return <StatusBadge status="moc_pending_finance" />;
      case 'fin_hold':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Clock size={14} /> Fin Hold</span>;
      case 'fin_rejected':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><AlertCircle size={14} /> Fin Rejected</span>;
      case 'moc_approved':
        return <StatusBadge status="moc_approved" />;
      case 'packing_queued':
        return <StatusBadge status="packing_queued" />;
      case 'packing_scheduled':
        return <StatusBadge status="packing_scheduled" />;
      case 'packing':
        return <span style={{ color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Package size={14} /> Packing In Progress</span>;
      case 'packed_ready':
        return <StatusBadge status="packed_ready" />;
      case 'invoice_pending':
        return <StatusBadge status="invoice_pending" />;
      case 'ewaybill_pending':
        return <StatusBadge status="ewaybill_pending" />;
      case 'invoice_ready':
        return <StatusBadge status="invoice_ready" />;
      case 'ewaybill_complete':
        return <StatusBadge status="ewaybill_complete" />;
      case 'dc_confirmed':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><CheckCircle size={14} /> Invoice Ready</span>;
      case 'shipped':
      case 'partially_shipped':
        return <span style={{ color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Truck size={14} /> Partially Shipped</span>;
      case 'in_transit':
        return <span style={{ color: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Clock size={14} /> In Transit</span>;
      case 'delivered':
        return <StatusBadge status="delivered" />;
      case 'canceled':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><X size={14} /> Canceled</span>;
      default:
        return <span style={{ color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}>{status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}</span>;
    }
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ marginBottom: '25px' }}>
        <h2 style={{ margin: 0 }}>MOC Credit Approval Queue</h2>
        <p style={{ color: '#64748b', marginTop: '5px' }}>Verify customer credit status for pending Material Out Checklists.</p>
      </div>

      {mocs.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <CheckCircle size={48} color="#10b981" style={{ marginBottom: '15px', opacity: 0.5 }} />
          <h3 style={{ color: '#374151' }}>Queue is Clear!</h3>
          <p style={{ color: '#64748b' }}>No MOCs are currently awaiting credit approval.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>MOC Date</th>
                <th style={{...thStyle, textAlign:'left'}}>Customer</th>
                <th style={thStyle}>Order No.</th>
                <th style={thStyle}>MOC No.</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Weight</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {mocs.map((moc) => (
                <tr 
                  key={moc.id} 
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => navigate(`/accounts/approvals/${moc.id}`)}
                  className="hover-highlight"
                >
                  <td style={tdStyle}>{formatDate(moc.mocDate)}</td>
                  <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{moc.customerName}</div>
                    <div style={{fontSize: '11px', color: '#6b7280'}}>{moc.typeOfMOC} MOC</div>
                  </td>
                  <td style={tdStyle}>{moc.orderNumber || '---'}</td>
                  <td style={tdStyle}>
                    <strong style={{fontSize: '13px'}}>{moc.mocNo}</strong>
                  </td>
                  <td style={tdStyle}>{moc.warehouse}</td>
                  <td style={tdStyle}>{moc.totalWeight?.toFixed(2)} kg</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      {renderStatus(moc.dispatchStatus)}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#2563eb', fontWeight: '600' }}>
                      Review <ChevronRight size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };

export default AccountsApprovals;
